import { z } from 'zod';
import { productKeySchema } from './products.js';
/**
 * Sign-in method contracts. The account hub shows ONLY methods that are configured for the selected
 * context. When the identity provider is not configured in an environment, the list is empty; a
 * Microsoft or Google button is never wired to a development fixture.
 */
export const loginMethodSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('organization-sso'),
        provider: z.enum(['microsoft', 'google', 'saml', 'oidc']),
        /** Identity-provider organization reference (an Auth0 organization id). Opaque, not a secret. */
        organizationRef: z.string(),
        connection: z.string(),
        /**
         * Complete button text, for example "Continue with Microsoft". Deliberately generic: it never names
         * the organization, so email discovery cannot reveal which company uses Trace behind a domain.
         */
        label: z.string(),
    }),
    z.object({ type: z.literal('microsoft'), connection: z.string() }),
    z.object({ type: z.literal('google'), connection: z.string() }),
    z.object({ type: z.literal('passwordless-email'), connection: z.string() }),
    z.object({ type: z.literal('password'), connection: z.string() }),
]);
/** GET /v1/login/options?product=<key> (the Origin header supplies the host). */
export const loginOptionsSchema = z.object({
    productKey: productKeySchema,
    identityProvider: z.object({
        configured: z.boolean(),
        /** Public SPA settings for this application, present only when configured. */
        domain: z.string().optional(),
        clientId: z.string().optional(),
        audience: z.string().optional(),
    }),
    /** Methods offered before an email is entered (for example Google and Microsoft social connections). */
    methods: z.array(loginMethodSchema),
    /** Whether email discovery should be offered (organization SSO routing by verified email domain). */
    emailDiscovery: z.boolean(),
    developmentIdentity: z.object({ enabled: z.boolean() }),
    /**
     * 0.20.0 (self-serve sign-up, spec 2026-10-18 piece 3): the database connection a new person creates an account on
     * (the account hub's /signup opens the identity provider's sign-up screens with it), or null when sign-up is not
     * offered. Non-null only for an employee product on Trace's own host when platform-api has LOGIN_SIGNUP_CONNECTION.
     * Defaults to null, so a client of this version still reads an older platform-api.
     */
    signup: z.object({ connection: z.string() }).nullable().default(null),
});
/** POST /v1/login/discover. Responses are deliberately neutral so they do not reveal membership. */
export const loginDiscoveryRequestSchema = z.object({
    email: z.email().max(320),
    productKey: productKeySchema,
});
export const loginDiscoveryResponseSchema = z.object({
    /**
     * When an organization has verified this email domain and enforces SSO, exactly that method is
     * returned and ssoEnforced is true. Otherwise the product's default methods come back unchanged,
     * whether or not the person exists.
     */
    methods: z.array(loginMethodSchema),
    ssoEnforced: z.boolean(),
});
//# sourceMappingURL=login.js.map